/**
 * TokenAuthPage - 인증 페이지
 *
 * @route /s/:token/auth
 *
 * 역할:
 * - Step 1: Activation Code 입력
 * - Step 2: Birth Date 확인
 * - 인증 완료 후 목록 페이지로 이동
 *
 * 비즈니스 로직은 useTokenAuthPage 훅에 위임
 */

import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from 'react-router-dom';
import { ActivationCodeStep } from './TokenSurvey/steps/ActivationCodeStep';
import { BirthDateStep } from './TokenSurvey/steps/BirthDateStep';
import { getSchemaForStep } from './TokenSurvey/validation/schemas';
import { useTokenAuthPage } from '@/hooks/useTokenAuthPage';
import { useSettings } from '@/contexts/SettingsContext';
import { useMatomo } from '@/hooks/useMatomo';
import type { WizardFormData } from './TokenSurvey/types';
import './TokenSurvey/TokenSurveyWizard.css';

export const TokenAuthPage = () => {
  // ── 인증 흐름 데이터 & 액션 ────────────────────────────────────────────
  const {
    token,
    state,
    authCodeConfig,
    handleStep1Complete,
    handleStep2Complete,
    handleBack,
    t,
  } = useTokenAuthPage();
  const { settings } = useSettings();
  const location = useLocation();
  const { trackEvent } = useMatomo();

  // ── Response Latency 추적 ──────────────────────────────────────────────
  // SMS 링크의 notif_ts 파라미터를 이용해 알림 발송 후 실제 응답까지의 지연 시간을 추적
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const notifTs = params.get('notif_ts');
    if (notifTs) {
      const latencyMinutes = Math.round(
        (Date.now() - parseInt(notifTs, 10) * 1000) / 60_000,
      );
      trackEvent('Notification', 'ResponseLatency', 'SMS', latencyMinutes);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 폼 상태 ───────────────────────────────────────────────────────────
  const currentSchema = getSchemaForStep(state.currentStep, settings.language);
  const form = useForm<WizardFormData>({
    resolver: currentSchema ? zodResolver(currentSchema) as unknown as Resolver<WizardFormData> : undefined,
    mode: 'onChange',
  });

  if (!token) {
    return <div>{t.noToken}</div>;
  }

  return (
    <div className="token-survey-wizard">
      <div className="wizard-content">
        <FormProvider {...form}>
          {state.currentStep === 'activation-code' ? (
            <ActivationCodeStep
              sessionId={token}
              onNext={handleStep1Complete}
              codeLength={authCodeConfig.codeLength}
              isAlphanumeric={authCodeConfig.isAlphanumeric}
            />
          ) : (
            <BirthDateStep
              sessionId={token}
              step1Token={state.step1Token!}
              participantMaskedName={state.participant?.masked_name || ''}
              onNext={handleStep2Complete}
              onBack={handleBack}
            />
          )}
        </FormProvider>
      </div>
    </div>
  );
};
